package com.trevorwiebe.timesheet.invoicing.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import com.trevorwiebe.timesheet.core.presentation.common.rememberLinkOpener
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun InvoicingScreen(
    onBack: (() -> Unit)?,
    viewModel: InvoicingViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val onEvent = viewModel::onEvent
    val openPdf = rememberLinkOpener(viewModel.state, { it.pdfUrlToOpen }, { it.isGeneratingPdf }) {
        onEvent(InvoicingEvent.OnPdfOpened)
    }
    val selected = state.selectedInvoice

    when {
        state.receivePayment != null -> ReceivePaymentContent(state = state, onEvent = onEvent)
        state.newInvoice != null -> NewInvoiceContent(state = state, onEvent = onEvent)
        selected != null -> InvoiceDetailContent(invoice = selected, state = state, openPdf = openPdf, onEvent = onEvent)
        else -> InvoiceListContent(state = state, onBack = onBack, onEvent = onEvent)
    }

    NativeDialog(
        visible = state.error != null,
        title = "Something went wrong",
        message = state.error,
        confirmText = "OK",
        onConfirm = { onEvent(InvoicingEvent.OnErrorShown) },
        onDismiss = { onEvent(InvoicingEvent.OnErrorShown) },
    )
}

@Composable
private fun InvoiceListContent(
    state: InvoicingState,
    onBack: (() -> Unit)?,
    onEvent: (InvoicingEvent) -> Unit,
) {
    Scaffold(
        topBar = {
            TopBar(
                title = "Invoicing",
                backIcon = if (onBack != null) ({ BackIcon() }) else null,
                onBack = { onBack?.invoke() },
                actions = {
                    TextButton(onClick = { onEvent(InvoicingEvent.OnReceivePaymentClick()) }) {
                        Text("Receive payment", color = MaterialTheme.colorScheme.tertiary)
                    }
                    TextButton(onClick = { onEvent(InvoicingEvent.OnNewInvoiceClick) }) {
                        Text("New invoice", color = MaterialTheme.colorScheme.tertiary)
                    }
                },
            )
        }
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            Column(Modifier.fillMaxSize()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    InvoiceListTab.entries.forEach { tab ->
                        val isSelected = tab == state.tab
                        FilterChip(
                            selected = isSelected,
                            onClick = { onEvent(InvoicingEvent.OnSelectTab(tab)) },
                            label = { Text(tab.label) },
                            border = FilterChipDefaults.filterChipBorder(
                                enabled = true,
                                borderColor = MaterialTheme.colorScheme.tertiary,
                                selected = isSelected,
                            ),
                            colors = FilterChipDefaults.filterChipColors(
                                containerColor = MaterialTheme.colorScheme.background,
                                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                                selectedLabelColor = MaterialTheme.colorScheme.onTertiary,
                            ),
                        )
                    }
                }
                HorizontalDivider()
                when {
                    state.isLoading -> Box(Modifier.fillMaxSize()) {
                        CircularProgressIndicator(
                            modifier = Modifier.align(Alignment.Center),
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                    state.visibleInvoices.isEmpty() -> Text(
                        text = emptyMessage(state.tab),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 32.dp),
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    )
                    else -> LazyColumn(Modifier.fillMaxSize()) {
                        items(state.visibleInvoices, key = { it.id }) { invoice ->
                            InvoiceRow(invoice = invoice, onClick = { onEvent(InvoicingEvent.OnInvoiceClick(invoice.id)) })
                        }
                    }
                }
            }
        }
    }
}

private fun emptyMessage(tab: InvoiceListTab): String = when (tab) {
    InvoiceListTab.DRAFTS -> "No draft invoices. Use New invoice to bill a customer's loads."
    InvoiceListTab.OPEN -> "No sent invoices are waiting on payment."
    InvoiceListTab.PAID -> "No paid invoices yet."
}

@Composable
private fun InvoiceRow(invoice: Invoice, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "${invoice.numberLabel}  ${invoice.customerName}",
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            Text(text = invoice.totalFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            val count = invoice.lines.size
            Text(
                text = "${Util.toFriendlyDate(invoice.invoiceDate)} • $count load${if (count == 1) "" else "s"}",
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                modifier = Modifier.weight(1f),
            )
            Text(
                text = invoice.statusLabel,
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.tertiary,
            )
        }
    }
    HorizontalDivider()
}
